import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Activity, Calendar, FileText, PencilIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DocumentDetailLayout,
  DocumentReviewDetailItem,
} from "@/components/request-detail";
import { useGetProjectKPIByIdQuery } from "@/services/(company)/api.projects";
import type { ISingleKPI } from "@/types/(company)/projects.interface";
import {
  projectIdKey,
  kpiIdKey,
} from "@/routes/company/routes.projects.constants";
import { PATHS } from "@/routes/paths";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useLocale } from "@/hooks/useLocale";
import { withBranding } from "@/utils/helper";

export const ProjectKPIDetailPage = () => {
  const { t } = useTranslation(["projects", "common"]);
  const { formatDate } = useLocale();
  usePageTitle(withBranding(t("projects:kpi_detail")));
  const navigate = useNavigate();
  const params = useParams();
  const projectId = params[projectIdKey];
  const kpiId = params[kpiIdKey];

  const { state } = useLocation() as { state: { kpi: ISingleKPI } | null };

  const { data, isLoading } = useGetProjectKPIByIdQuery(
    { projectId: projectId ?? "", kpiId: kpiId ?? "" },
    { skip: !projectId || !kpiId },
  );

  // API data is authoritative; state is instant fallback while loading
  const kpi = data?.data ?? state?.kpi;

  if (!projectId || !kpiId) return null;

  return (
    <DocumentDetailLayout
      title={t("projects:kpi_detail")}
      status={kpi?.status}
      onBack={() => navigate(-1)}
      isLoading={isLoading && !state?.kpi}
      footer={
        kpi ? (
          <>
            <Button variant="outline" onClick={() => navigate(-1)}>
              {t("common:buttons.back")}
            </Button>
            <Button
              variant="primary"
              onClick={() =>
                navigate(
                  PATHS.COMPANY.COMPANY_PROJECTS_PATHS.EDIT_KPI(
                    projectId,
                    kpiId,
                  ),
                )
              }
            >
              <PencilIcon className="w-4 h-4" />
              {t("projects:edit")}
            </Button>
          </>
        ) : undefined
      }
    >
      {kpi ? (
        <>
          <h5 className="mt-3 mb-8 font-medium">
            {t("projects:kpi_information")}
          </h5>
          <div className="flex flex-col w-full mb-8 gap-7">
            <DocumentReviewDetailItem
              icon={<FileText className="w-5 h-5 text-primary" />}
              color="primary/10"
              label={t("projects:kpi_name")}
              value={kpi.name}
              valueClassName="font-medium"
            />
            {kpi.description && (
              <DocumentReviewDetailItem
                icon={<FileText className="w-5 h-5 text-primary" />}
                color="primary/10"
                label={t("projects:description")}
                value={kpi.description}
                valueClassName="font-medium"
              />
            )}
            <DocumentReviewDetailItem
              icon={<Activity className="w-5 h-5 text-primary" />}
              color="primary/10"
              label={t("projects:status")}
              value={t(`projects:${kpi.status}`)}
              valueClassName="font-medium uppercase"
            />
            <DocumentReviewDetailItem
              icon={<Calendar className="w-5 h-5 text-[#1AD598]" />}
              color="[#1AD5981A]"
              label={t("projects:created_on")}
              value={formatDate(new Date(kpi.createdAt), "dd MMM yyyy")}
              valueClassName="font-medium"
            />
          </div>
        </>
      ) : (
        !isLoading && (
          <p className="text-sm text-muted-foreground mt-6">
            {t("projects:kpi_not_found")}
          </p>
        )
      )}
    </DocumentDetailLayout>
  );
};
